import { formatDateForDisplay, formatDateTimeForDisplay } from '../utils/timezone';

// Shared styles for the entire application
export const sharedStyles = {
  // Layout
  pageContainer: {
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
    padding: '2rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1a1f36',
    marginBottom: '1.5rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    letterSpacing: '-0.02em'
  },

  // Cards
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1)'
    }
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1)'
    }
  },

  // Typography
  cardTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1a1f36',
    marginBottom: '1rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    letterSpacing: '-0.01em'
  },
  cardSubtitle: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#697386',
    marginBottom: '0.5rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  statValue: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#1a1f36',
    marginBottom: '0.25rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    letterSpacing: '-0.02em'
  },
  statLabel: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#697386',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },

  // Badges and Status
  badge: {
    padding: '0.5em 0.8em',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '0.75rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  statusBadge: {
    padding: '0.4em 0.8em',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '0.75rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },

  // Tables
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    '& th': {
      backgroundColor: '#f5f7fa',
      color: '#1a1f36',
      fontWeight: '600',
      fontSize: '0.875rem',
      padding: '1rem',
      borderBottom: '2px solid #e5e9f0',
      letterSpacing: '-0.01em'
    },
    '& td': {
      padding: '1rem',
      borderBottom: '1px solid #e5e9f0',
      color: '#1a1f36',
      fontSize: '0.875rem',
      fontWeight: '400'
    },
    '& tr:hover': {
      backgroundColor: '#f5f7fa'
    }
  },

  // Forms
  formLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#1a1f36',
    marginBottom: '0.5rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  formControl: {
    borderRadius: '8px',
    border: '1px solid #e5e9f0',
    padding: '0.625rem 0.875rem',
    fontSize: '0.875rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:focus': {
      borderColor: '#911311',
      boxShadow: '0 0 0 3px rgba(145, 19, 17, 0.1)'
    }
  },
  formSelect: {
    borderRadius: '8px',
    border: '1px solid #e5e9f0',
    padding: '0.625rem 0.875rem',
    fontSize: '0.875rem',
    backgroundColor: '#ffffff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:focus': {
      borderColor: '#911311',
      boxShadow: '0 0 0 3px rgba(145, 19, 17, 0.1)'
    }
  },

  // Buttons
  button: {
    primary: {
      backgroundColor: '#911311',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '0.625rem 1rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#7a0f0e',
        transform: 'translateY(-1px)'
      }
    },
    secondary: {
      backgroundColor: '#ffffff',
      color: '#1a1f36',
      border: '1px solid #e5e9f0',
      borderRadius: '8px',
      padding: '0.625rem 1rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#f5f7fa',
        borderColor: '#d1d5db',
        transform: 'translateY(-1px)'
      }
    },
    danger: {
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '0.625rem 1rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#dc2626',
        transform: 'translateY(-1px)'
      }
    }
  },

  // Icons
  icon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem'
  },

  // Colors
  colors: {
    primary: {
      main: '#911311',
      light: '#fde8e8',
      dark: '#7a0f0e'
    },
    success: {
      main: '#10b981',
      light: '#d1fae5',
      dark: '#059669'
    },
    warning: {
      main: '#f59e0b',
      light: '#fef3c7',
      dark: '#d97706'
    },
    danger: {
      main: '#ef4444',
      light: '#fee2e2',
      dark: '#dc2626'
    },
    info: {
      main: '#3b82f6',
      light: '#dbeafe',
      dark: '#2563eb'
    },
    text: {
      primary: '#1a1f36',
      secondary: '#697386',
      disabled: '#9ca3af'
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff'
    },
    border: '#e5e9f0'
  },

  // Modal
  modal: {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(4px)'
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      border: 'none',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1)',
      padding: '1.5rem'
    }
  },

  // Progress bars
  progressBar: {
    height: '8px',
    borderRadius: '4px',
    backgroundColor: '#e5e9f0',
    overflow: 'hidden',
    '& .progress-bar': {
      borderRadius: '4px',
      transition: 'width 0.3s ease-in-out'
    }
  },

  // Pagination
  pagination: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    '& .page-item': {
      '& .page-link': {
        padding: '0.5rem 0.75rem',
        borderRadius: '8px',
        border: '1px solid #e5e9f0',
        color: '#1a1f36',
        fontSize: '0.875rem',
        fontWeight: '500',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '#f5f7fa',
          borderColor: '#d1d5db'
        }
      },
      '&.active .page-link': {
        backgroundColor: '#911311',
        borderColor: '#911311',
        color: '#ffffff'
      }
    }
  }
};

// Helper functions for status colors
export const getStatusColor = (status) => {
  const statusColors = {
    'Active': { bg: sharedStyles.colors.success.light, text: sharedStyles.colors.success.main },
    'Inactive': { bg: sharedStyles.colors.danger.light, text: sharedStyles.colors.danger.main },
    'Pending': { bg: sharedStyles.colors.warning.light, text: sharedStyles.colors.warning.main },
    'Completed': { bg: sharedStyles.colors.success.light, text: sharedStyles.colors.success.main },
    'In Progress': { bg: sharedStyles.colors.primary.light, text: sharedStyles.colors.primary.main },
    'On Hold': { bg: sharedStyles.colors.warning.light, text: sharedStyles.colors.warning.main },
    'Paid': { bg: sharedStyles.colors.success.light, text: sharedStyles.colors.success.main },
    'Approved': { bg: sharedStyles.colors.info.light, text: sharedStyles.colors.info.main },
    'Invoiced': { bg: '#e8f4f8', text: '#0c7496' },
    'Rejected': { bg: sharedStyles.colors.danger.light, text: sharedStyles.colors.danger.main },
    '15%': { bg: '#e3f2fd', text: '#1976d2' },
    '30%': { bg: '#bbdefb', text: '#1565c0' },
    '60%': { bg: '#90caf9', text: '#0d47a1' },
    '90%': { bg: '#64b5f6', text: '#01579b' }
  };
  return statusColors[status] || { bg: '#f5f7fa', text: '#697386' };
};

// Helper function for formatting currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper function for formatting dates
export const formatDate = (date) => {
  return formatDateForDisplay(date);
};

// Helper function for formatting date times
export const formatDateTime = (date) => {
  return formatDateTimeForDisplay(date);
};

// Helper function for truncating text
export const truncateText = (text, length = 50) => {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}; 